import React, { useCallback, useEffect, useState } from 'react';
import { apiCall } from '../config';
import { toast, confirmDialog } from '../ui/notify';

// Leiste über der Schichtplanung (große UND Handy-Ansicht) – Philipp, 04.10.2026:
//  ⚠️ Hinweise (Besetzung, Kasse, Minderjährige, Ruhezeit, lange Schichten)  – Admin, Einsatzleitung, Stations-Planer
//  ✉️ Nachrichten (offene Änderungen je Person → „Schicht geändert", EINE Nachricht je Person)   – nur Admin
//  📝 Änderungen (Protokoll + Rückgängig) · 🪄 Planvorschlag · 🙋 Freie Schichten anbieten · 🚫 Abwesenheit – nur Admin
// Gesendet wird nur nach Klick auf „Senden" mit genau dem angezeigten Text.
const J = { method: 'POST', headers: { 'Content-Type': 'application/json' } };
const post = (url, body) => apiCall(url, { ...J, body: JSON.stringify(body || {}) });
const dot = { gut: '🟢', normal: '🟡', schlecht: '🔴' };
const timeOf = (at) => { try { return new Date(String(at).replace(' ', 'T') + 'Z').toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch (_) { return at; } };
const fullText = (firstname, tag, was) => `Hallo ${firstname}, es gibt eine Änderung an deiner Schicht am ${tag}:\n${was}\n\nBitte bestätige kurz, dass du Bescheid weißt. Wenn es nicht passt, tippe auf „Es gibt ein Problem".\n[Gesehen] [Es gibt ein Problem]`;

export default function PlanningInsights({ date, user, refreshKey, onChanged }) {
  const isAdmin = user?.role === 'admin';
  const [open, setOpen] = useState(null);
  const [warn, setWarn] = useState(null);
  const [pending, setPending] = useState([]);
  const [edits, setEdits] = useState({});       // user_id → { tag, was }
  const [changes, setChanges] = useState([]);
  const [chScope, setChScope] = useState('day');
  const [sugg, setSugg] = useState(null);
  const [pick, setPick] = useState({});
  const [offer, setOffer] = useState(null);
  const [offerPick, setOfferPick] = useState({});
  const [dayRows, setDayRows] = useState([]);
  const [absId, setAbsId] = useState('');
  const [busy, setBusy] = useState(false);

  const loadCounts = useCallback(async () => {
    apiCall(`/planning/warnings/${date}`).then(setWarn).catch(() => setWarn(null));
    if (isAdmin) apiCall('/admin/planning/notify-preview').then((p) => setPending(Array.isArray(p) ? p : [])).catch(() => setPending([]));
  }, [date, isAdmin]);
  const loadChanges = useCallback(async () => {
    if (!isAdmin) return;
    apiCall(chScope === 'day' ? `/admin/planning/changes?date=${date}` : '/admin/planning/changes?hours=24').then((c) => setChanges(Array.isArray(c) ? c : [])).catch(() => setChanges([]));
  }, [date, chScope, isAdmin]);

  useEffect(() => { loadCounts(); }, [loadCounts, refreshKey]);
  useEffect(() => { if (open === 'changes') loadChanges(); }, [open, loadChanges, refreshKey]);
  useEffect(() => { setSugg(null); setOffer(null); setAbsId(''); }, [date]);
  useEffect(() => { if (open === 'absence') apiCall(`/planning/assignments/${date}`).then((r) => setDayRows(Array.isArray(r) ? r : [])).catch(() => setDayRows([])); }, [open, date, refreshKey]);

  const changed = () => { loadCounts(); loadChanges(); if (onChanged) onChanged(); };
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast(e.message || 'Fehlgeschlagen', 'danger'); } finally { setBusy(false); } };

  const W = (warn && warn.warnings) || [];
  const serious = W.filter((w) => w.level !== 'info');
  const chip = (key, label, count, cls) => (
    <button key={key} type="button" className={`btn btn-sm ${open === key ? 'btn-dark' : 'btn-outline-secondary'}`} onClick={() => setOpen(open === key ? null : key)}>
      {label}{count != null && count > 0 && <span className={`badge ms-1 ${cls || 'bg-secondary'}`}>{count}</span>}
    </button>
  );

  const edit = (p) => edits[p.user_id] || { tag: p.tag, was: p.was };

  return (
    <div className="card mb-3">
      <div className="card-body py-2">
        <div className="d-flex flex-wrap gap-2 align-items-center">
          {chip('warn', '⚠️ Hinweise', serious.length, serious.some((w) => w.level === 'danger') ? 'bg-danger' : 'bg-warning text-dark')}
          {isAdmin && chip('notify', '✉️ Nachrichten', pending.length, 'bg-primary')}
          {isAdmin && chip('changes', '📝 Änderungen')}
          {isAdmin && chip('suggest', '🪄 Planvorschlag')}
          {isAdmin && chip('offer', '🙋 Freie Schichten anbieten')}
          {isAdmin && chip('absence', '🚫 Abwesenheit')}
        </div>

        {open === 'warn' && (
          <div className="mt-2 small">
            {!warn ? <div className="text-muted">lädt …</div> : !W.length ? <div className="text-success">✅ Keine Hinweise für diesen Tag.</div> : (
              <>
                {!warn.hasPlan && <div className="text-muted mb-1">Für diesen Tag ist kein Bedarf hinterlegt – Besetzung und Kasse werden nicht geprüft.</div>}
                {[['danger', 'text-danger'], ['warning', ''], ['info', 'text-muted']].map(([lvl, cls]) => W.filter((w) => w.level === lvl).map((w, i) => (
                  <div key={lvl + i} className={cls}>{lvl === 'danger' ? '🔴' : lvl === 'warning' ? '🟡' : '⚪'} {w.text}</div>
                )))}
              </>
            )}
          </div>
        )}

        {open === 'notify' && (
          <div className="mt-2">
            {!pending.length ? <div className="small text-muted">Keine offenen Änderungen – alle Betroffenen sind informiert (oder es hat sich nichts geändert).</div> : pending.map((p) => {
              const e = edit(p);
              return (
                <div key={p.user_id} className="border rounded p-2 mb-2">
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                    <strong>{p.name}</strong>
                    <span className={`badge ${p.channel === 'chat' ? 'bg-success' : p.channel === 'template' ? 'bg-info text-dark' : 'bg-secondary'}`}>{p.channel === 'chat' ? 'Chat-Nachricht' : p.channel === 'template' ? 'Vorlage „Schicht geändert"' : 'keine WhatsApp-Nummer'}</span>
                    {p.revoked && <span className="badge bg-warning text-dark">hat WhatsApp-Infos abgemeldet</span>}
                  </div>
                  <div className="row g-2 mb-1">
                    <div className="col-md-3"><input className="form-control form-control-sm" value={e.tag} onChange={(ev) => setEdits({ ...edits, [p.user_id]: { ...e, tag: ev.target.value } })} /></div>
                    <div className="col-md-9"><textarea className="form-control form-control-sm" rows={2} value={e.was} onChange={(ev) => setEdits({ ...edits, [p.user_id]: { ...e, was: ev.target.value.replace(/\n/g, ' ') } })} /></div>
                  </div>
                  <pre className="small bg-light p-2 mb-2" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{fullText(p.firstname, e.tag, e.was)}</pre>
                  <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-primary" disabled={busy || p.channel === 'none'} onClick={async () => {
                      if (!(await confirmDialog(`Diese Nachricht jetzt an ${p.name} senden?${p.revoked ? ' Achtung: Die Person hat WhatsApp-Infos abgemeldet.' : ''}`))) return;
                      run(async () => { await post('/admin/planning/notify', { user_id: p.user_id, tag: e.tag, was: e.was }); toast(`Nachricht an ${p.name} gesendet`, 'success'); loadCounts(); });
                    }}>Senden</button>
                    <button className="btn btn-sm btn-outline-secondary" disabled={busy} title="Ich sage es der Person selbst" onClick={() => run(async () => { await post('/admin/planning/notify-skip', { user_id: p.user_id }); loadCounts(); })}>Nicht nötig</button>
                  </div>
                </div>
              );
            })}
            <div className="small text-muted">Jede Person bekommt eine Nachricht mit allen offenen Änderungen. Der Text lässt sich vor dem Senden anpassen (z.B. „krankheitsbedingt").</div>
          </div>
        )}

        {open === 'changes' && (
          <div className="mt-2">
            <div className="btn-group btn-group-sm mb-2">
              <button className={`btn ${chScope === 'day' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setChScope('day')}>Dieser Tag</button>
              <button className={`btn ${chScope === '24h' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setChScope('24h')}>Letzte 24 Stunden (alle Tage)</button>
            </div>
            {!changes.length ? <div className="small text-muted">Keine Änderungen erfasst. Das Protokoll läuft seit dem 04.10.2026.</div> : (
              <div className="table-responsive" style={{ maxHeight: 320, overflowY: 'auto' }}>
                <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                  <tbody>{changes.map((c) => (
                    <tr key={c.id} className={c.undone ? 'text-muted' : ''} style={c.undone ? { textDecoration: 'line-through' } : undefined}>
                      <td className="text-nowrap">{timeOf(c.at)}</td>
                      <td className="text-nowrap">{c.by}</td>
                      <td><strong>{c.who}</strong> · {c.day} · {c.text}{c.isUndo && <span className="badge bg-light text-dark ms-1">Rückgängig</span>}</td>
                      <td className="text-end">{!c.undone && (
                        <button className="btn btn-sm btn-outline-secondary py-0" disabled={busy} onClick={async () => {
                          if (!(await confirmDialog(`Rückgängig machen? ${c.who}, ${c.day}: ${c.text}`))) return;
                          run(async () => { await post(`/admin/planning/changes/${c.id}/undo`); toast('Rückgängig gemacht', 'success'); changed(); });
                        }}>↺</button>
                      )}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {open === 'suggest' && (
          <div className="mt-2">
            {!sugg ? (
              <button className="btn btn-sm btn-outline-primary" disabled={busy} onClick={() => run(async () => { const r = await apiCall(`/admin/planning/suggest/${date}`); setSugg(r); setPick(Object.fromEntries((r.proposals || []).map((_, i) => [i, true]))); })}>Vorschlag für diesen Tag berechnen</button>
            ) : !sugg.hasPlan ? <div className="small text-muted">Für diesen Tag ist kein Bedarf hinterlegt.</div> : (
              <>
                {!sugg.proposals.length && <div className="small text-success">✅ Nichts vorzuschlagen – der Bedarf ist gedeckt oder es ist niemand Passendes verfügbar.</div>}
                {sugg.proposals.map((p, i) => (
                  <label key={i} className="d-flex gap-2 align-items-start small mb-1" style={{ cursor: 'pointer' }}>
                    <input type="checkbox" className="form-check-input mt-1" checked={!!pick[i]} onChange={(e) => setPick({ ...pick, [i]: e.target.checked })} />
                    <span><strong>{p.station} {p.start_time}–{p.end_time}</strong> {p.name}{p.type === 'extend' ? <span className="badge bg-light text-dark ms-1">verlängern (bisher {p.old})</span> : <span className="badge bg-light text-dark ms-1">neu</span>}{p.cashier && ' 💶'}
                      <span className="text-muted d-block">{p.reason}</span></span>
                  </label>
                ))}
                {sugg.unfilled.map((u, i) => <div key={'u' + i} className="small text-danger">offen: {u.text}</div>)}
                <div className="d-flex gap-2 mt-2">
                  {sugg.proposals.length > 0 && <button className="btn btn-sm btn-success" disabled={busy || !Object.values(pick).some(Boolean)} onClick={() => run(async () => {
                    const sel = sugg.proposals.filter((_, i) => pick[i]);
                    const r = await post(`/admin/planning/suggest/${date}/apply`, { proposals: sel });
                    const bad = (r.results || []).filter((x) => !x.ok);
                    toast(bad.length ? `${sel.length - bad.length} übernommen, ${bad.length} nicht: ${bad.map((b) => `${b.name} (${b.error})`).join(', ')}` : `${sel.length} Einsätze übernommen`, bad.length ? 'warning' : 'success');
                    setSugg(null); changed();
                  })}>Ausgewählte übernehmen</button>}
                  <button className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => setSugg(null)}>Verwerfen</button>
                </div>
                <div className="small text-muted mt-1">Nur ein Entwurf: Es wird erst beim Übernehmen etwas eingetragen, Nachrichten gehen dabei keine raus (die stehen danach unter ✉️).</div>
              </>
            )}
          </div>
        )}

        {open === 'offer' && (
          <div className="mt-2">
            {!offer ? (
              <button className="btn btn-sm btn-outline-primary" disabled={busy} onClick={() => run(async () => { setOffer(await apiCall(`/admin/planning/offer-candidates?date=${date}`)); setOfferPick({}); })}>Passende Leute für diesen Tag suchen</button>
            ) : (
              <>
                <div className="small mb-1">{offer.offers.length ? <>Offen in der Schichtbörse: {offer.offers.map((o) => `${o.station} ${o.from}–${o.to}`).join(', ')}</> : <span className="text-muted">Für diesen Tag steht nichts in der Schichtbörse (Lücken unter 3 Stunden werden dort nicht angeboten).</span>}</div>
                {offer.offers.length > 0 && (
                  <>
                    <pre className="small bg-light p-2 mb-2" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{offer.text}{'\n[Freie Schichten ansehen]'}</pre>
                    {!offer.candidates.length && <div className="small text-muted">Niemand hat an dem Tag Zeit eingetragen und noch nichts vor.</div>}
                    {offer.candidates.map((c) => (
                      <label key={c.user_id} className="d-flex gap-2 align-items-start small mb-1" style={{ cursor: 'pointer' }}>
                        <input type="checkbox" className="form-check-input mt-1" checked={!!offerPick[c.user_id]} onChange={(e) => setOfferPick({ ...offerPick, [c.user_id]: e.target.checked })} />
                        <span>{dot[c.rating] || '•'} <strong>{c.name}</strong> · verfügbar {c.available}{c.planned && ` · schon eingeplant ${c.planned}`}
                          <span className="text-muted"> · {c.channel === 'chat' ? 'Chat' : 'Vorlage'}{c.lastSent && ` · zuletzt angeschrieben ${timeOf(c.lastSent)}`}</span>
                          {c.revoked && <span className="badge bg-warning text-dark ms-1">abgemeldet</span>}</span>
                      </label>
                    ))}
                    <div className="d-flex gap-2 mt-2">
                      <button className="btn btn-sm btn-primary" disabled={busy || !Object.values(offerPick).some(Boolean)} onClick={async () => {
                        const ids = Object.keys(offerPick).filter((k) => offerPick[k]).map(Number);
                        if (!(await confirmDialog(`Diese Nachricht an ${ids.length} ${ids.length === 1 ? 'Person' : 'Personen'} senden? Jede Nachricht kostet.`))) return;
                        run(async () => { const r = await post('/admin/planning/offer', { date, user_ids: ids }); const bad = (r.results || []).filter((x) => !x.ok).length; toast(bad ? `${ids.length - bad} gesendet, ${bad} fehlgeschlagen` : `${ids.length} Nachrichten gesendet`, bad ? 'warning' : 'success'); setOffer(null); });
                      }}>An Ausgewählte senden</button>
                      <button className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => setOffer(null)}>Schließen</button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {open === 'absence' && (
          <div className="mt-2 d-flex flex-wrap gap-2 align-items-center">
            <select className="form-select form-select-sm" style={{ maxWidth: 340 }} value={absId} onChange={(e) => setAbsId(e.target.value)}>
              <option value="">Einsatz wählen …</option>
              {dayRows.filter((a) => a.status !== 'cancelled').map((a) => <option key={a.id} value={a.id}>{a.user_name} · {String(a.station_id || '').toUpperCase()} {a.start_time}–{a.end_time}{a.absence ? ` · ${a.absence === 'sick' ? 'krank' : 'nicht erschienen'}` : ''}</option>)}
            </select>
            {[['no_show', 'Nicht erschienen', 'btn-outline-danger'], ['sick', 'Krank', 'btn-outline-warning'], [null, 'Zurücknehmen', 'btn-outline-secondary']].map(([kind, label, cls]) => (
              <button key={label} className={`btn btn-sm ${cls}`} disabled={busy || !absId} onClick={() => run(async () => { await post('/timesheet-entries/absence', { assignment_id: Number(absId), kind }); toast(kind ? `Als „${label.toLowerCase()}" markiert` : 'Abwesenheit zurückgenommen', 'success'); changed(); })}>{label}</button>
            ))}
            <div className="small text-muted w-100">Der Einsatz bleibt im Plan, zählt aber nicht mehr als Besetzung und fällt aus der Prüfung „geplant ohne Ist-Stunden". Die Lücke erscheint unter ⚠️.</div>
          </div>
        )}
      </div>
    </div>
  );
}
